import { ChangeDetectionStrategy, Component, computed, inject, output, viewChild } from '@angular/core';
import {
  AlertController,
  IonButton,
  IonNote,
  LoadingController,
  ToastController,
} from '@ionic/angular/standalone';

import { LoadingMessage } from '../../../../../common/enums/loading-message.enum';
import { SyncMessage } from '../../../../../common/enums/sync-message.enum';
import { ToastLife } from '../../../../../common/enums/toast-life.enum';
import { LogFactory } from '../../../../../common/services/logger/log-factory';
import {
  DeletableItem,
  DeletedItemsListComponent,
} from '../deleted-items-list/deleted-items-list.component';
import { ItemSizeService } from '../../services/item-size.service';
import { BackupSpaceService } from '../../services/backup-space.service';
import { ItemFailureReason, SyncService } from '../../sync/sync.service';
import { timeAgo } from '../../../../../common/utils/time-ago.util';

/** Why it failed, in the user's terms — the row has one line to say it. */
const REASON_LABEL: Record<ItemFailureReason, string> = {
  storage: SyncMessage.FailedReasonStorage,
  media: SyncMessage.FailedReasonMedia,
  push: SyncMessage.FailedReasonPush,
};

/**
 * "Failed backups": the items whose last backup attempt didn't make it, with a
 * way to retry just those and a way to stop being told about them.
 *
 * The red badge on a library card already says an item failed, but only one card
 * at a time and only where the user happens to be looking. This is the list that
 * answers "what is not in the cloud", which is the question someone asks after a
 * backup reports that three items couldn't go.
 *
 * Rows are joined from the sync ledger (which records ids, not names) against
 * the local library. An id with no local row left, or one now in the recycle
 * bin, is dropped AND forgotten: the backup will never retry it, so keeping it
 * on a list of things to retry would be a permanent dead end.
 *
 * "Remove" here destroys nothing anywhere — hence the non-destructive styling
 * and the confirm that says so. The item simply goes back to showing as not
 * backed up, which it is.
 */
@Component({
  selector: 'app-failed-backups-section',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonButton, IonNote, DeletedItemsListComponent],
  template: `
    @if (rows().length) {
      <div class="section-title">
        <h2>{{ Title }}</h2>
        @if (list()?.selecting()) {
          <ion-button size="small" fill="clear" (click)="list()!.exitSelectMode()">
            {{ Cancel }}
          </ion-button>
        } @else {
          <ion-button size="small" fill="clear" (click)="list()!.enterSelectMode()" data-testid="fb-select">
            {{ Select }}
          </ion-button>
        }
      </div>

      <ion-note class="intro-note">{{ Intro }}</ion-note>

      <app-deleted-items-list
        [items]="rows()"
        [emptyText]="Empty"
        [showRestore]="true"
        [restoreLabel]="RetryLabel"
        restoreIcon="refresh-outline"
        [deleteLabel]="RemoveLabel"
        deleteIcon="close-outline"
        [deleteIsDestructive]="false"
        (restoreOne)="retry([$event])"
        (restoreMany)="retryMany($event)"
        (deleteOne)="dismiss([$event])"
        (deleteMany)="dismissMany($event)"
      ></app-deleted-items-list>
    }
  `,
  styles: [`
    :host { display: block; }
    .section-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      margin: 18px 0 8px;
    }
    .section-title h2 {
      font-size: 14px;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--ion-color-danger);
      margin: 0;
    }
    .section-title ion-button { margin: 0; --padding-start: 6px; --padding-end: 6px; }
    .intro-note { display: block; margin-bottom: 12px; font-size: 13px; line-height: 1.4; }
  `],
})
export class FailedBackupsSectionComponent {
  private readonly sync = inject(SyncService);
  private readonly backupSpace = inject(BackupSpaceService);
  private readonly itemSizes = inject(ItemSizeService);
  private readonly alertCtrl = inject(AlertController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly toastCtrl = inject(ToastController);
  private readonly logger = inject(LogFactory).createLogger(FailedBackupsSectionComponent);

  /** Optional: the list is only rendered while something has failed. */
  protected readonly list = viewChild(DeletedItemsListComponent);

  /** A retry may have moved items into the cloud — the sheet's figures shift. */
  readonly changed = output<void>();

  protected readonly Title = SyncMessage.FailedBackupsTitle;
  protected readonly Intro = SyncMessage.FailedBackupsIntro;
  protected readonly Empty = SyncMessage.FailedBackupsEmpty;
  protected readonly RetryLabel = SyncMessage.RetryButton;
  protected readonly RemoveLabel = SyncMessage.RemoveFromListButton;
  protected readonly Select = SyncMessage.CloudItemsSelect;
  protected readonly Cancel = SyncMessage.Cancel;

  /**
   * Failed ids joined to the local library. Ids with no live local row are
   * dropped here and forgotten on the next interaction — see `prune`.
   */
  protected readonly rows = computed<DeletableItem[]>(() => {
    const byId = this.itemSizes.itemsById();
    const out: DeletableItem[] = [];
    for (const failure of this.sync.failedItems()) {
      const item = byId.get(failure.id);
      if (!item || item.deletedAt) continue;
      out.push({
        kind: item.kind,
        id: item.id,
        name: item.name,
        imageMediaId: item.imageMediaId,
        note: this.noteFor(failure.reason, failure.at),
      });
    }
    return out;
  });

  protected async retryMany(items: DeletableItem[]): Promise<void> {
    await this.retry(items);
    this.list()?.exitSelectMode();
  }

  protected async retry(items: DeletableItem[]): Promise<void> {
    if (!items.length) return;
    this.prune();
    const choice = await this.backupSpace.ask(items.map((i) => ({ kind: i.kind, id: i.id })));
    if (!choice) return;
    const loading = await this.presentLoading(LoadingMessage.Retrying);
    try {
      const ran = await this.sync.retryFailedItems(items.map((i) => i.id));
      if (!ran) {
        await this.toast(SyncMessage.BackupFailedPrefix, 'danger');
        return;
      }
      const err = this.sync.lastError();
      if (err) {
        await this.toast(err, 'danger');
      } else {
        await this.toast(SyncMessage.BackupComplete, 'success');
        // A retry that worked is a backup like any other, so it honours the same
        // choice — this section sits on the backup screen, and behaving
        // differently from the button above it would be arbitrary.
        await this.backupSpace.freeIfChosen(choice, [...this.sync.lastBackedUp()]);
      }
    } catch (err) {
      this.logger.error('Retrying failed backups did not complete', err);
      await this.toast(SyncMessage.BackupFailedPrefix, 'danger');
    } finally {
      await loading.dismiss();
      this.changed.emit();
    }
  }

  protected async dismissMany(items: DeletableItem[]): Promise<void> {
    await this.dismiss(items);
    this.list()?.exitSelectMode();
  }

  protected async dismiss(items: DeletableItem[]): Promise<void> {
    if (!items.length) return;
    const alert = await this.alertCtrl.create({
      header: SyncMessage.RemoveFromListHeader,
      message: SyncMessage.RemoveFromListMessage,
      buttons: [
        { text: SyncMessage.Cancel, role: 'cancel' },
        { text: SyncMessage.RemoveFromListButton, role: 'confirm' },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    if (role !== 'confirm') return;
    this.sync.dismissFailedItems(items.map((i) => i.id));
    await this.toast(SyncMessage.RemovedFromList);
  }

  /** Forget failures whose item is gone from the library or now in the recycle
   *  bin: a backup will never send those, so they can only sit here forever. */
  private prune(): void {
    const byId = this.itemSizes.itemsById();
    const stale = this.sync
      .failedItems()
      .filter((f) => {
        const item = byId.get(f.id);
        return !item || !!item.deletedAt;
      })
      .map((f) => f.id);
    if (stale.length) this.sync.dismissFailedItems(stale);
  }

  private noteFor(reason: ItemFailureReason | undefined, at: number | undefined): string {
    const why = reason ? REASON_LABEL[reason] : SyncMessage.BackupFailedPrefix;
    return at ? `${why} · ${timeAgo(at)}` : why;
  }

  private presentLoading(message: string) {
    return this.loadingCtrl
      .create({ message, spinner: 'crescent', backdropDismiss: false })
      .then(async (loading) => {
        await loading.present();
        return loading;
      });
  }

  private async toast(message: string, color = 'medium'): Promise<void> {
    const t = await this.toastCtrl.create({
      message,
      color,
      duration: ToastLife.SHORT,
      position: 'bottom',
    });
    await t.present();
  }
}
